import { COLORS } from '@/styles/tokens/colors';

import type { GraphRole, GraphSpace } from './householdLoadingGraph.types';

/** Input candidates; composition applies its own visible-node limit. */
export const PREVIEW_CONFIG = {
  ILLUSTRATIVE_NODE_COUNT: 48,
  ILLUSTRATIVE_KIND_COUNT: 4,
  ILLUSTRATIVE_HUB_COUNT: 6,
  MAX_PREVIEW_NODE_CANDIDATES: 120,
};

/** Force-layout tuning, in simulation units. */
export const FORCE_CONFIG = {
  REPULSION: 230,
  GRAVITY: 0.0011,
  DAMPING: 0.86,
  REPULSION_RANGE: 300,
  SETTLE_STEPS: 400,
  SEED_DISTANCE: 30,
  FRAME_PADDING: 32,
  MIN_FRAME_EXTENT: 160,
  MAX_FRAME_SCALE: 2.5,
  CHARGE: { hub: 3.2, primary: 1.3, minor: 1 } satisfies Record<
    GraphRole,
    number
  >,
  SPRINGS: {
    hub: { length: 175, strength: 0.012 },
    branch: { length: 58, strength: 0.03 },
    leaf: { length: 34, strength: 0.03 },
    cross: { length: 150, strength: 0.0025 },
  },
};

export const WIDE_GRAPH_SPACE: GraphSpace = { width: 880, height: 480 };
export const COMPACT_GRAPH_SPACE: GraphSpace = { width: 380, height: 460 };

/** Visible nodes, relationship selection, and label geometry in SVG units. */
export const COMPOSITION_CONFIG = {
  ROLE_RADIUS: { hub: 5.5, primary: 3, minor: 2 } satisfies Record<
    GraphRole,
    number
  >,
  MAX_VISIBLE_NODES: 100,
  MAX_HUBS: 12,
  MAX_CROSS_LINKS: 12,
  /** Wide screens show more names and relationships, within the same node limit. */
  WIDE_CONFIG: {
    maxNamed: 60,
    maxEdgeLabels: 6,
    labelFontSize: 12.5,
    labelMaxChars: 21,
    labelMaxLines: 3,
    hubLabelMaxChars: 24,
    edgeLabelFontSize: 12,
    edgeLabelMaxChars: 22,
  },
  COMPACT_CONFIG: {
    maxNamed: 30,
    maxEdgeLabels: 3,
    labelFontSize: 11.5,
    labelMaxChars: 16,
    labelMaxLines: 3,
    hubLabelMaxChars: 22,
    edgeLabelFontSize: 11.5,
    edgeLabelMaxChars: 22,
  },
  /** Space between a node's edge and its label. */
  LABEL_GAP: 7,
  LABEL_PADDING: 5,
  /** Average glyph width divided by font size for the UI sans-serif font. */
  GLYPH_WIDTH_RATIO: 0.56,
  LINE_HEIGHT_RATIO: 1.25,
  /** Distance from a line's top to its baseline, divided by font size. */
  ASCENT_RATIO: 0.95,
};

/** Rendering and animation tuning. Distances are SVG units; durations are ms. */
export const RENDER_CONFIG = {
  /** Keep wide-layout name labels above 11px after scaling to the container. */
  COMPACT_CONTAINER_MAX_WIDTH: 800,
  COMPACT_GRAPH_MAX_WIDTH: 440,
  /** Time for a node to cover about two thirds of the distance to its target. */
  NODE_EASE_MS: 650,
  /** Limit the frame step so returning from a background tab cannot jump. */
  MAX_FRAME_MS: 50,
  /** Curves and labels follow the swaying nodes so everything stays attached. */
  SATELLITE_SWAY: 3.4,
  HUB_SWAY: 1.2,
  SWAY_PERIOD_MS: 10000,
  PARTICLE_PERIOD_MS: 7000,
  PARTICLE_COUNT: 2,
  HUB_HALO_RADIUS: 16,
  MINOR_OPACITY: 0.75,
  SPOKE_OPACITY: 0.18,
  RIM_OPACITY: 0.1,
  EDGE_COLOR: COLORS.TEXT.PRIMARY,
  NAME_COLOR: COLORS.TEXT.PRIMARY,
  RELATIONSHIP_COLOR: COLORS.TEXT.SECONDARY,
  /**
   * Shared brand palette, used categorically rather than for status or risk. Kinds
   * receive colors in first-appearance order and retain them as this graph grows.
   * A given color has no fixed meaning across households. Names carry identity.
   */
  KIND_COLORS: [
    COLORS.NAVY[500],
    COLORS.TEAL[700],
    COLORS.ORANGE[400],
    COLORS.BLUE[700],
    COLORS.NAVY[700],
    COLORS.TEAL[600],
    COLORS.ORANGE[700],
    COLORS.BLUE[500],
  ],
};
